// src/pages/driver/DriverEditPage.js
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Alert,
  Breadcrumbs,
  Link,
  Button,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getDriver, updateDriver } from '../../api/driverAPI';
import DriverForm from '../../components/driver/DriverForm';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const DriverEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const driverId = parseInt(id);

  // Fetch driver details
  const { data: driver, isLoading: isLoadingDriver, isError, error } = useQuery({
    queryKey: ['driver', driverId],
    queryFn: () => getDriver(driverId),
    enabled: !!driverId && !isNaN(driverId)
  });

  // Update driver mutation
  const mutation = useMutation({
    mutationFn: (data) => updateDriver(driverId, data),
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['driver', driverId] });
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      
      // If driver group was changed, also invalidate driver group queries
      if (driver?.DriverCompanyId) {
        queryClient.invalidateQueries({ queryKey: ['company-driver-groups', driver.DriverCompanyId] });
        queryClient.invalidateQueries({ queryKey: ['driver-group-drivers'] });
      }
      
      setSnackbar({
        open: true,
        message: 'Driver updated successfully!',
        severity: 'success'
      });
      
      // Navigate back to driver details after a brief delay
      setTimeout(() => {
        navigate(`/drivers/${driverId}`);
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating driver: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleSubmit = async (data) => {
    try {
      await mutation.mutateAsync(data);
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoadingDriver) {
    return <LoadingSpinner />;
  }

  if (isError || !driver) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading driver'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4">Edit {driver.DriverFullName}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Update driver information and group assignment
          </Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to={`/drivers/${driverId}`} 
          startIcon={<ArrowBackIcon />}
        >
          Back to Driver Details
        </Button>
      </Box>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {mutation.error.message}
        </Alert>
      )}

      <DriverForm 
        driver={driver}
        companyId={driver.DriverCompanyId}
        onSubmit={handleSubmit} 
        isLoading={mutation.isPending}
        showDriverGroupSelection={true}  
      />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverEditPage;